import type { ChartData, ChartOptions } from 'chart.js';

import type { ResultDistributionType } from 'types/api/diagnosisReportRefResponseType';
import { formatNumberToCurrency } from 'utils/app';

export const COLOR_PALETTE: readonly string[] = [
  '#4A90E2', // 青
  '#28a745', // 緑
  '#ffc107', // オレンジ
  '#e91e63', // 赤
  '#17a2b8', // シアン
  '#6f42c1', // 紫
  '#fd7e14', // オレンジ2
  '#20c997', // ティール
] as const;

export function computeTotalCount(items: ResultDistributionType[]): number {
  return items.reduce((s, i) => s + i.count, 0);
}

export function computePercentages(items: ResultDistributionType[], totalCount: number): number[] {
  return totalCount > 0
    ? items.map(i => Math.round((i.count / totalCount) * 100))
    : items.map(() => 0);
}

export function getColor(index: number): string {
  return COLOR_PALETTE[index % COLOR_PALETTE.length] as string;
}

export function buildStackedBarData(
  items: ResultDistributionType[],
  totalCount: number
): ChartData<'bar'> {
  const percentages = computePercentages(items, totalCount);
  return {
    labels: [''],
    datasets: items.map((item, idx) => ({
      data: [percentages[idx] ?? 0],
      backgroundColor: getColor(idx),
      borderColor: 'rgba(0,0,0,0.15)',
      borderWidth: 1,
      stack: 'stack1',
    })),
  };
}

export function buildStackedBarOptions(
  items: ResultDistributionType[],
  totalCount: number,
  unit: string
): ChartOptions<'bar'> {
  const percentages = computePercentages(items, totalCount);
  const labels = items.map(i => i.title);
  const line2Strings = items.map(
    (i, idx) => `${formatNumberToCurrency(i.count)}${unit} (${percentages[idx]}%)`
  );
  return {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
      stackedBarSegmentLabels: {
        labels,
        line2Strings,
        percentages,
      },
    },
    scales: {
      x: {
        stacked: true,
        max: 100,
        ticks: {
          callback: (value: number | string) => (typeof value === 'number' ? `${value}%` : value),
        },
        grid: { display: true },
      },
      y: {
        stacked: true,
        display: false,
      },
    },
  };
}

export function buildLegendItems(
  items: ResultDistributionType[],
  totalCount: number,
  unit: string
): { title: string; valueLabel: string; color: string; percentage: string }[] {
  return items.map((i, idx) => {
    const percentage = totalCount > 0 ? ((i.count / totalCount) * 100).toFixed(0) : '0';
    const valueLabel = `${formatNumberToCurrency(i.count)}${unit} (${percentage}%)`;
    return {
      title: i.title,
      valueLabel,
      color: getColor(idx),
      percentage,
    };
  });
}

/**
 * ctx 非依存のテキスト折り返し関数。
 * measure 関数で文字幅を取得する（ctx.measureText(s).width を注入する想定）。
 */
export function wrapLabelLines(
  text: string,
  maxWidth: number,
  measure: (s: string) => number,
  opts?: { maxLines?: number }
): string[] {
  const maxLines = opts?.maxLines ?? 3;
  const lines: string[] = [];
  let current = '';
  for (const char of text) {
    const test = current + char;
    if (measure(test) <= maxWidth) {
      current = test;
    } else {
      if (current) lines.push(current);
      current = char;
    }
  }
  if (current) lines.push(current);
  if (lines.length > maxLines) {
    const lastLine = lines[maxLines - 1] ?? '';
    let short = lastLine;
    while (short.length && measure(short + '…') > maxWidth) {
      short = short.slice(0, -1);
    }
    return [...lines.slice(0, maxLines - 1), short + '…'];
  }
  return lines;
}
